import React, { useContext, useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RestoreIcon from '@mui/icons-material/Restore'
import { firebase_firestore } from '../../../config/firebaseConfig'
import { doc, updateDoc, arrayUnion, arrayRemove, collection, query } from 'firebase/firestore'
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore'
import { SnackbarHelperContext } from '../../../common/contexts/SnackbarHelperContext'
import { FeatureTag, FEATURE_TAG_CATEGORIES, FEATURE_TAG_LABELS } from '../../constants/featureTags'
import { computeEffectiveTags, getTagSource, TagSource } from '../../utils/computeEffectiveTags'

type TagState = 'enabled' | 'disabled' | 'off'

const TAG_STATE_COLORS: Record<TagState, string> = {
  enabled: '#1DC30E',
  disabled: '#f44336',
  off: '#666',
}

interface UserFeatureTagsProps {
  trader: any
  onTraderUpdate?: (updatedTrader: any) => void
}

export default function UserFeatureTags({ trader, onTraderUpdate }: UserFeatureTagsProps) {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const [loadingTags, setLoadingTags] = useState<Record<string, boolean>>({})
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuTarget, setMenuTarget] = useState<FeatureTag | null>(null)

  // Fetch products to get product tags
  const [products, productsLoading] = useCollectionDataOnce(
    query(collection(firebase_firestore, 'products'))
  )

  // Get the user's current product based on their subscription
  const userProductName = trader?.products?.alerts?.name

  // Find the matching product document
  const userProduct = useMemo(() => {
    if (!products || !userProductName) return null
    return products.find(
      (p: any) =>
        p.name?.toLowerCase() === userProductName?.toLowerCase() ||
        p.id?.toLowerCase() === userProductName?.toLowerCase()
    )
  }, [products, userProductName])

  // Get product tags and denyTags (from product document)
  const productTags: FeatureTag[] = useMemo(() => {
    return userProduct?.tags || []
  }, [userProduct])

  const productDenyTags: FeatureTag[] = useMemo(() => {
    return userProduct?.denyTags || []
  }, [userProduct])

  // Get user override tags
  const userTags: string[] = useMemo(() => {
    return trader?.products?.tags || []
  }, [trader?.products?.tags])

  // Get user deny tags
  const denyTags: string[] = useMemo(() => {
    return trader?.products?.denyTags || []
  }, [trader?.products?.denyTags])

  // Compute effective tags
  const effectiveTags = useMemo(() => {
    const status =
      trader?.tradingHouses?.greenchart?.currentStatus?.status ||
      trader?.tradingHouses?.gtf?.currentStatus?.status
    const gtfStatus = trader?.tradingHouses?.gtf?.currentStatus?.status

    return computeEffectiveTags({
      status,
      productTags,
      productDenyTags,
      userTags,
      denyTags,
      tradingProductEnabled: (trader?.products?.alerts?.maxAutoTradeAlertsAllowed ?? 0) > 0,
      gtfStatus,
    })
  }, [productTags, productDenyTags, userTags, denyTags, trader])

  const handleAccordionChange =
    (category: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCategories((prev) =>
        isExpanded ? [...prev, category] : prev.filter((c) => c !== category)
      )
    }

  // Get the user-level state for a tag (not considering product defaults)
  const getUserTagState = (tag: FeatureTag): TagState => {
    const normalizedTag = tag.toLowerCase()
    const normalizedUserTags = userTags.map((t) => t.toLowerCase())
    const normalizedDenyTags = denyTags.map((t) => t.toLowerCase())

    if (normalizedDenyTags.includes(normalizedTag)) return 'disabled'
    if (normalizedUserTags.includes(normalizedTag)) return 'enabled'
    return 'off'
  }

  const handleTagStateChange = async (tag: FeatureTag, newState: TagState) => {
    setMenuAnchor(null)
    setMenuTarget(null)

    const userDocRef = doc(firebase_firestore, `usermeta/${trader.gcid}`)
    setLoadingTags((prev) => ({ ...prev, [tag]: true }))

    try {
      // Remove tag from both arrays first, then add to appropriate array based on new state
      const updates: Record<string, any> = {}

      // Always remove from both arrays first
      if (userTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
        updates['products.tags'] = arrayRemove(tag)
      }
      if (denyTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
        updates['products.denyTags'] = arrayRemove(tag)
      }

      // Apply removals first if needed
      if (Object.keys(updates).length > 0) {
        await updateDoc(userDocRef, updates)
      }

      // Now add to appropriate array based on new state
      if (newState === 'enabled') {
        await updateDoc(userDocRef, {
          'products.tags': arrayUnion(tag),
        })
      } else if (newState === 'disabled') {
        await updateDoc(userDocRef, {
          'products.denyTags': arrayUnion(tag),
        })
      }
      // 'off' state means we just leave it removed from both arrays

      // Update local trader object
      if (onTraderUpdate) {
        const updatedProducts = { ...trader.products }
        // Remove from both arrays
        updatedProducts.tags = (updatedProducts.tags || []).filter(
          (t: string) => t.toLowerCase() !== tag.toLowerCase()
        )
        updatedProducts.denyTags = (updatedProducts.denyTags || []).filter(
          (t: string) => t.toLowerCase() !== tag.toLowerCase()
        )
        // Add to appropriate array
        if (newState === 'enabled') {
          updatedProducts.tags = [...updatedProducts.tags, tag]
        } else if (newState === 'disabled') {
          updatedProducts.denyTags = [...updatedProducts.denyTags, tag]
        }
        onTraderUpdate({ ...trader, products: updatedProducts })
      }

      const stateLabels = {
        enabled: 'enabled',
        disabled: 'disabled',
        off: 'reset to product default',
      }
      setSnackbarSettings({
        severity: 'success',
        message: `Feature tag ${stateLabels[newState]} successfully!`,
        autoHideDuration: 3000,
      })
    } catch (err: any) {
      console.error('Error updating tag:', err)
      setSnackbarSettings({
        severity: 'error',
        message: `Error updating feature tag`,
        autoHideDuration: 6000,
      })
    } finally {
      setLoadingTags((prev) => ({ ...prev, [tag]: false }))
    }
  }

  const handleResetTag = (tag: FeatureTag) => {
    handleTagStateChange(tag, 'off')
  }

  const openTagMenu = (event: React.MouseEvent<HTMLElement>, tag: FeatureTag) => {
    setMenuAnchor(event.currentTarget)
    setMenuTarget(tag)
  }

  const getSourceChip = (source: TagSource) => {
    switch (source) {
      case 'product':
        return <Chip size="small" label="Product Default" color="primary" variant="outlined" />
      case 'user':
        return <Chip size="small" label="User Added" color="success" variant="outlined" />
      case 'denied':
        return <Chip size="small" label="Denied" color="error" variant="outlined" />
      default:
        return null
    }
  }

  const hasOverride = (tag: FeatureTag): boolean => {
    const normalizedTag = tag.toLowerCase()
    const normalizedUserTags = userTags.map((t) => t.toLowerCase())
    const normalizedDenyTags = denyTags.map((t) => t.toLowerCase())
    return normalizedUserTags.includes(normalizedTag) || normalizedDenyTags.includes(normalizedTag)
  }

  if (productsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: '0.775rem',
          fontWeight: 600,
          color: '#8c939b',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          mb: 0.25,
        }}
      >
        Feature Tags
      </Typography>

      {!userProductName && (
        <Typography sx={{ fontSize: '0.775rem', color: '#b8860b', mb: 0.5 }}>
          No product subscription found
        </Typography>
      )}

      {FEATURE_TAG_CATEGORIES.map((category) => (
        <Accordion
          key={category.name}
          expanded={expandedCategories.includes(category.name)}
          onChange={handleAccordionChange(category.name)}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 500 }}>{category.name}</Typography>
            <Typography sx={{ ml: 'auto', mr: 2, color: 'text.secondary' }}>
              {category.tags.filter((tag) => effectiveTags.has(tag)).length}/{category.tags.length}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {category.tags.map((tag) => {
                const source = getTagSource(tag, productTags, userTags, denyTags)
                const userState = getUserTagState(tag)
                const isProductDefault = productTags.includes(tag)
                const isLoading = loadingTags[tag]
                const isOverridden = hasOverride(tag)

                // Determine effective state for display
                // If user has override, show that state; otherwise show product default
                let effectiveState: TagState
                if (userState !== 'off') {
                  effectiveState = userState
                } else {
                  effectiveState = isProductDefault ? 'enabled' : 'off'
                }

                return (
                  <Box
                    key={tag}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: isOverridden ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{FEATURE_TAG_LABELS[tag] || tag}</Typography>
                      {getSourceChip(source)}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isOverridden && (
                        <Tooltip title="Reset to product default">
                          <IconButton
                            size="small"
                            onClick={() => handleResetTag(tag)}
                            disabled={isLoading}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isLoading ? (
                        <CircularProgress size={20} sx={{ mx: 1 }} />
                      ) : (
                        <Box
                          onClick={(e) => openTagMenu(e, tag)}
                          sx={{
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            minWidth: 80,
                            textAlign: 'center',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              color: TAG_STATE_COLORS[effectiveState],
                              textTransform: 'uppercase',
                              fontSize: '0.75rem',
                              letterSpacing: 0.5,
                            }}
                          >
                            {effectiveState}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Tag state menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null)
          setMenuTarget(null)
        }}
      >
        {(['enabled', 'disabled', 'off'] as TagState[]).map((state) => (
          <MenuItem
            key={state}
            onClick={() => menuTarget && handleTagStateChange(menuTarget, state)}
            sx={{ minWidth: 120 }}
          >
            <Typography
              sx={{ color: TAG_STATE_COLORS[state], fontWeight: 500, textTransform: 'capitalize' }}
            >
              {state === 'off' ? 'Off (Product Default)' : state}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
